import React, { useEffect, useState } from 'react';

const DateRangeSelector = ({ startDate, endDate, onStartDateChange, onEndDateChange, availableMonths, compact = false }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 768 && window.innerWidth > 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
      setIsTablet(window.innerWidth <= 768 && window.innerWidth > 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getLastDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  const handleStartDateChange = (newDate) => {
    const firstDay = getFirstDayOfMonth(newDate);
    const lastDay = getLastDayOfMonth(newDate);
    
    // Ensure both dates are in the same month
    onStartDateChange(firstDay);
    // Automatically adjust end date to the last day of the same month
    if (endDate.getMonth() !== firstDay.getMonth() || endDate.getFullYear() !== firstDay.getFullYear()) {
      onEndDateChange(lastDay);
    }
  };

  const handleEndDateChange = (newDate) => {
    const firstDay = getFirstDayOfMonth(newDate);
    const lastDay = getLastDayOfMonth(newDate);
    
    // Ensure both dates are in the same month
    onEndDateChange(lastDay);
    // Automatically adjust start date to the first day of the same month
    if (startDate.getMonth() !== firstDay.getMonth() || startDate.getFullYear() !== firstDay.getFullYear()) {
      onStartDateChange(firstDay);
    }
  };

  const handlePrevMonth = () => {
    const currentIndex = availableMonths.findIndex(
      d => d.getMonth() === startDate.getMonth() && d.getFullYear() === startDate.getFullYear()
    );
    if (currentIndex > 0) {
      const prevMonth = availableMonths[currentIndex - 1];
      handleStartDateChange(prevMonth);
      handleEndDateChange(prevMonth);
    }
  };

  const handleNextMonth = () => {
    const currentIndex = availableMonths.findIndex(
      d => d.getMonth() === startDate.getMonth() && d.getFullYear() === startDate.getFullYear()
    );
    if (currentIndex < availableMonths.length - 1) {
      const nextMonth = availableMonths[currentIndex + 1];
      handleStartDateChange(nextMonth);
      handleEndDateChange(nextMonth);
    }
  };

  const currentIndex = availableMonths.findIndex(
    d => d.getMonth() === startDate.getMonth() && d.getFullYear() === startDate.getFullYear()
  );

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Ensure dates are always in the same month on mount
  useEffect(() => {
    const startMonth = startDate.getMonth();
    const startYear = startDate.getFullYear();
    const endMonth = endDate.getMonth();
    const endYear = endDate.getFullYear();

    // If dates are in different months, align them to the start date's month
    if (startMonth !== endMonth || startYear !== endYear) {
      const firstDay = getFirstDayOfMonth(startDate);
      const lastDay = getLastDayOfMonth(startDate);
      // Only update if the dates are actually different from what we're setting
      if (startDate.getTime() !== firstDay.getTime()) {
        onStartDateChange(firstDay);
      }
      if (endDate.getTime() !== lastDay.getTime()) {
        onEndDateChange(lastDay);
      }
    }
  }, []); // Only run on mount to fix initial misalignment

  const containerStyle = compact ? {
    display: 'flex',
    alignItems: 'center',
    gap: isMobile ? '0.25rem' : '0.35rem',
    padding: 0,
    background: 'transparent',
    borderTop: 'none',
    marginTop: 0,
    flexWrap: isMobile ? 'wrap' : 'nowrap'
  } : {
    display: 'flex',
    alignItems: isMobile ? 'stretch' : 'center',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '0.5rem' : (isTablet ? '0.4rem' : '0.5rem'),
    padding: isMobile ? '0.5rem' : (isTablet ? '0.4rem 0.6rem' : '0.5rem 0.75rem'),
    background: '#f8f9fa',
    borderTop: '1px solid #dee2e6',
    marginTop: 'auto',
    flexWrap: isMobile ? 'wrap' : 'nowrap'
  };

  const buttonStyle = compact ? {
    padding: isMobile ? '0.2rem 0.4rem' : '0.25rem 0.5rem',
    fontSize: isMobile ? '0.75rem' : '0.85rem',
    minWidth: isMobile ? '32px' : '28px',
    minHeight: isMobile ? '32px' : 'auto'
  } : {
    padding: isMobile ? '0.4rem 0.5rem' : (isTablet ? '0.3rem 0.5rem' : '0.3rem 0.6rem'),
    fontSize: isMobile ? '0.85rem' : (isTablet ? '0.9rem' : '0.95rem'),
    minWidth: isMobile ? '36px' : (isTablet ? '32px' : '32px'),
    minHeight: isMobile ? '36px' : 'auto'
  };

  const labelStyle = compact ? {
    fontSize: isMobile ? '0.65rem' : '0.7rem',
    fontWeight: 600,
    color: '#495057',
    whiteSpace: isMobile ? 'normal' : 'nowrap'
  } : {
    fontSize: isMobile ? '0.7rem' : (isTablet ? '0.72rem' : '0.75rem'),
    fontWeight: 600,
    color: '#495057',
    whiteSpace: isMobile ? 'normal' : 'nowrap',
    minWidth: isMobile ? 'auto' : 'fit-content'
  };

  const selectStyle = compact ? {
    padding: isMobile ? '0.3rem 0.4rem' : '0.25rem 0.35rem',
    fontSize: isMobile ? '0.7rem' : '0.7rem',
    border: '1px solid #ced4da',
    borderRadius: '3px',
    background: 'white',
    cursor: 'pointer',
    flex: 1,
    minWidth: 0,
    minHeight: isMobile ? '36px' : 'auto',
    width: isMobile ? '100%' : 'auto'
  } : {
    padding: isMobile ? '0.4rem 0.5rem' : (isTablet ? '0.35rem 0.45rem' : '0.3rem 0.4rem'),
    fontSize: isMobile ? '0.75rem' : (isTablet ? '0.73rem' : '0.75rem'),
    border: '1px solid #ced4da',
    borderRadius: '3px',
    background: 'white',
    cursor: 'pointer',
    flex: isMobile ? '1 1 100%' : 1,
    minWidth: isMobile ? '100%' : (isTablet ? '100px' : '120px'),
    minHeight: isMobile ? '36px' : 'auto',
    width: isMobile ? '100%' : 'auto'
  };

  return (
    <div className="date-range-selector" style={containerStyle}>
      <button
        onClick={handlePrevMonth}
        disabled={currentIndex === 0}
        style={{
          ...buttonStyle,
          background: currentIndex === 0 ? '#e9ecef' : '#3498db',
          color: currentIndex === 0 ? '#adb5bd' : 'white',
          border: 'none',
          borderRadius: '3px',
          cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
          fontWeight: 'bold',
          transition: 'all 0.2s',
          flexShrink: 0,
          order: isMobile ? 1 : 0
        }}
        aria-label="Previous month"
      >
        ‹
      </button>

      <div style={{ 
        display: 'flex', 
        alignItems: isMobile ? 'stretch' : 'center', 
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '0.5rem' : (compact ? '0.35rem' : '0.5rem'), 
        flex: 1,
        width: isMobile ? '100%' : 'auto'
      }}>
        {isMobile ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', width: '100%' }}>
              <label style={labelStyle}>
                Start Date:
              </label>
              <select
                value={`${startDate.getFullYear()}-${startDate.getMonth()}`}
                onChange={(e) => {
                  const [year, month] = e.target.value.split('-');
                  handleStartDateChange(new Date(parseInt(year), parseInt(month), 1));
                }}
                style={selectStyle}
              >
                {availableMonths.map((month, index) => (
                  <option key={index} value={`${month.getFullYear()}-${month.getMonth()}`}>
                    {formatDate(getFirstDayOfMonth(month))}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', width: '100%' }}>
              <label style={labelStyle}>
                End Date:
              </label>
              <select
                value={`${endDate.getFullYear()}-${endDate.getMonth()}`}
                onChange={(e) => {
                  const [year, month] = e.target.value.split('-');
                  handleEndDateChange(new Date(parseInt(year), parseInt(month), 1));
                }}
                style={selectStyle}
              >
                {availableMonths.map((month, index) => (
                  <option key={index} value={`${month.getFullYear()}-${month.getMonth()}`}>
                    {formatDate(getLastDayOfMonth(month))}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <>
            <label style={labelStyle}>
              Start Date:
            </label>
            <select
              value={`${startDate.getFullYear()}-${startDate.getMonth()}`}
              onChange={(e) => {
                const [year, month] = e.target.value.split('-');
                handleStartDateChange(new Date(parseInt(year), parseInt(month), 1));
              }}
              style={selectStyle}
            >
              {availableMonths.map((month, index) => (
                <option key={index} value={`${month.getFullYear()}-${month.getMonth()}`}>
                  {formatDate(getFirstDayOfMonth(month))}
                </option>
              ))}
            </select>

            <label style={labelStyle}>
              End Date:
            </label>
            <select
              value={`${endDate.getFullYear()}-${endDate.getMonth()}`}
              onChange={(e) => {
                const [year, month] = e.target.value.split('-');
                handleEndDateChange(new Date(parseInt(year), parseInt(month), 1));
              }}
              style={selectStyle}
            >
              {availableMonths.map((month, index) => (
                <option key={index} value={`${month.getFullYear()}-${month.getMonth()}`}>
                  {formatDate(getLastDayOfMonth(month))}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      <button
        onClick={handleNextMonth}
        disabled={currentIndex === availableMonths.length - 1}
        style={{
          ...buttonStyle,
          background: currentIndex === availableMonths.length - 1 ? '#e9ecef' : '#3498db',
          color: currentIndex === availableMonths.length - 1 ? '#adb5bd' : 'white',
          border: 'none',
          borderRadius: '3px',
          cursor: currentIndex === availableMonths.length - 1 ? 'not-allowed' : 'pointer',
          fontWeight: 'bold',
          transition: 'all 0.2s',
          flexShrink: 0,
          order: isMobile ? 2 : 0
        }}
        aria-label="Next month"
      >
        ›
      </button>
    </div>
  );
};

export default DateRangeSelector;
